import { SELECTORS, CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Alert module
 */
export default class Alert extends MessageBus {
  /**
   * Constructor function of the Alert class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.KEY = SELECTORS.CARE_ALERT_BANNER_HIDDEN;

    this.init();
  }

  /**
   * Initializes the alert by caching DOM elements,
   * setting up handlers, and enabling events.
   * @returns {Alert} A reference to the current instance
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {Alert} A reference to the current instance
   * @chainable
   */
  cacheDomReferences() {
    this.alertElement = document.querySelector(`.${CLASS_NAMES.ALERT}`);
    this.closeButton = this.element.querySelector(
      `.${CLASS_NAMES.ALERT_CLOSE}`
    );
    this.alertIcon = document.querySelector(`.${CLASS_NAMES.ALERT_ICON}`);
    this.body = document.body;
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {Alert} A reference to the current instance
   * @chainable
   */
  setupHandlers() {
    this.hideAlertEvent = this.hideAlert.bind(this);
    return this;
  }

  /**
   * Enable event listeners
   * @returns {Alert} A reference to the current instance
   * @chainable
   */
  enable() {
    if (sessionStorage.getItem(this.KEY) === 'true') {
      this.hideAlert();
      this.handleResize();
      if (this.alertIcon) this.alertIcon.style.display = 'none';
    } else {
      this.alertElement.classList.add(CLASS_NAMES.IS_VISIBLE);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener(EVENTS.CLICK, () => {
        this.hideAlert();
        this.handleResize();
        sessionStorage.setItem(this.KEY, 'true');
      });
    }

    window.addEventListener(EVENTS.RESIZE, this.handleResize.bind(this));
    window.addEventListener(EVENTS.SCROLL, this.handleResize.bind(this));
    window.addEventListener(EVENTS.LOAD, this.handleResize.bind(this));

    return this;
  }

  /**
   * Hide the alert
   */
  hideAlert() {
    if (this.alertElement) {
      this.alertElement.classList.remove(CLASS_NAMES.IS_VISIBLE);
    }

    this.body.classList.remove(`${CLASS_NAMES.ALERT_ACTIVE}`);

    const heroSelectors = [
      `.${CLASS_NAMES.INTERIOR_HERO}`,
      `.${CLASS_NAMES.NEWS_HERO}`,
      `.${CLASS_NAMES.CONTENT_DETAIL_HERO}`,
      `.${CLASS_NAMES.LANDING_HERO}`
    ];
    heroSelectors.forEach((selector) => {
      const heroEl = document.querySelector(selector);
      if (heroEl && heroEl.dataset.baseMarginTop !== undefined) {
        heroEl.style.marginTop = `${heroEl.dataset.baseMarginTop}px`;
      }
    });

    const textWrapper = document.querySelector(
      `.${CLASS_NAMES.INTERIOR_HERO_TEXT_WRAPPER}`
    );
    if (textWrapper && textWrapper.dataset.baseTop !== undefined) {
      textWrapper.style.top = `${textWrapper.dataset.baseTop}px`;
    }
  }

  /**
   * Set alert padding based on screen size
   */
  setAlertPadding() {
    const alertHeight = this.alertElement ? this.alertElement.offsetHeight : 0;
    const basePadding = window.innerWidth < 1024 ? 65 : 120;
    this.body.style.paddingTop = this.body.classList.contains(
      `${CLASS_NAMES.ALERT_ACTIVE}`
    )
      ? `${basePadding + alertHeight}px`
      : `${basePadding}px`;
  }

  /**
   * Adjust margin-top of hero elements based on alert height
   */
  adjustHeroMargin() {
    if (!this.alertElement || this.alertElement.style.display === 'none')
      return;

    const alertHeight = this.alertElement.offsetHeight;
    const textWrapper = document.querySelector(
      `.${CLASS_NAMES.INTERIOR_HERO_TEXT_WRAPPER}`
    );
    if (textWrapper) {
      if (!textWrapper.dataset.baseTop) {
        const currentTop = window.getComputedStyle(textWrapper).top;
        textWrapper.dataset.baseTop = parseFloat(currentTop) || 0;
      }

      const baseTop = parseFloat(textWrapper.dataset.baseTop);
      textWrapper.style.top = `${baseTop + alertHeight}px`;
    }
  }

  handleResize() {
    if (this.alertElement) {
      const bodyHeight =
        document.offsetHeight || document.documentElement.offsetHeight || 0;
      document.body.style.setProperty('--height-body', `${bodyHeight}px`);
      document.body.classList.add('has-height-body');
    }
  }
}
